import { ColumnInterface } from "components/tables/interfaces";
import { OrderSorter } from "components/tables/sort";
import Table from "components/tables/table";
import { useLocale } from "stores/hooks/locale";
import { parse } from "lib/locale";
import { Fragment, FunctionComponent, useEffect, useState } from "react";
import { showError, showSuccess } from "lib/notification";
import { ListEntityField } from "models/list-entity-field";
import {
  adminDoDeleteListEntityField,
  adminDoGetListEntityFields,
  adminDoPatchListEntityFieldOrder,
} from "services/admin/list-entity-field";
import AddEntityFieldModal from "./add-entity-field-modal";
import EditEntityFieldModal from "./edit-entity-field-modal";
import { useTranslate } from "stores/hooks/translation";
import { List } from "models/list";
import { faLock } from "components/icon-library";

interface ListEntityFieldsTableProps {
  list: List;
  entityUuid: string;
}

const ListEntityFieldsTable: FunctionComponent<ListEntityFieldsTableProps> = ({
  list,
  entityUuid,
}) => {
  const translate = useTranslate();
  const [fields, toggleFields] = useState<ListEntityField[]>([]);
  const locale = useLocale();
  const [addModal, toggleAddModal] = useState(false);
  const [editModal, toggleEditModal] = useState<ListEntityField | undefined>(
    undefined,
  );

  const update = async () => {
    const fields = await adminDoGetListEntityFields(list, entityUuid);
    toggleFields(fields.filter((f) => f !== null));
  };

  useEffect(() => {
    update();
  }, []);

  const columns: ColumnInterface[] = [
    {
      key: "name",
      header: "Kenttä",
      render: (field: ListEntityField) => parse(field.name, locale),
    },
    {
      key: "datatype",
      header: "Tyyppi",
      render: (field: ListEntityField) => {
        return translate(`field_datatype_${field.datatype}`);
      },
      width: 150,
    },
    {
      key: "required",
      header: "Pakolllinen",
      render: (field: ListEntityField) => {
        return field.required ? "Kyllä" : "Ei";
      },
      width: 100,
    },
  ];

  const handleOpenField = (field: ListEntityField) => {
    toggleEditModal(field);
  };

  const handleDelete = async (field: ListEntityField) => {
    try {
      await adminDoDeleteListEntityField(list, entityUuid, field.uuid);
      showSuccess("Kentän poistaminen onnistui.");
    } catch (err) {
      showError("Kentän poistaminen epäonnistui.");
    }
    update();
  };

  const handleUp = async (field: ListEntityField) => {
    try {
      const { order } = field;
      await adminDoPatchListEntityFieldOrder(
        list,
        entityUuid,
        field.uuid,
        order - 1,
      );
      showSuccess("Järjestyksen vaihtaminen onnistui.");

      update();
    } catch (err) {
      // nada
      showError("Järjestyksen vaihtaminen epäonnistui.");
    }
  };

  const handleDown = async (field: ListEntityField) => {
    try {
      const { order } = field;
      await adminDoPatchListEntityFieldOrder(
        list,
        entityUuid,
        field.uuid,
        order + 1,
      );

      update();
    } catch (err) {
      // nada
    }
  };

  return (
    <Fragment>
      <Table
        name={`list-${list.uuid}-entity-${entityUuid}-fields`}
        columns={columns}
        sortFn={OrderSorter}
        index
        editers={[{ onClick: async (entity) => handleOpenField(entity) }]}
        data={fields}
        noItemsText="Ei kenttiä"
        adder={{
          onClick: async () => toggleAddModal(true),
          text: "Uusi kenttä",
        }}
        statuses={[
          { icon: faLock, check: (item: ListEntityField) => item.locked },
        ]}
        mover={{
          isUpDisabled: (item: ListEntityField) => item.order === 0,
          isDownDisabled: (item: ListEntityField) =>
            item.order === fields.length - 1,
          onUp: handleUp,
          onDown: handleDown,
        }}
        deleter={{
          onClick: handleDelete,
          isDisabled: (field: ListEntityField) =>
            fields.length < 2 || field.locked,
        }}
      />
      {addModal && (
        <AddEntityFieldModal
          list={list}
          entityUuid={entityUuid}
          existingIdentifiers={fields.map((e) => e.identifier)}
          onChange={update}
          onClose={async () => {
            toggleAddModal(false);
          }}
        />
      )}
      {editModal && (
        <EditEntityFieldModal
          list={list}
          entityUuid={entityUuid}
          field={editModal}
          existingIdentifiers={fields
            .map((e) => e.identifier)
            // remove identifier of the current field
            .filter((e) => e !== editModal.identifier)}
          onChange={update}
          onClose={async () => {
            toggleEditModal(undefined);
          }}
        />
      )}
    </Fragment>
  );
};

export default ListEntityFieldsTable;
